import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Avatar, SkeletonHero, ErrorMessage, usePageMeta } from '../components/ui';
import { brawlerImage, initial, pct, timeAgo } from '../data/meta';
import { RecentTags, rememberTag } from '../components/RecentTags';

function resultClass(r) {
  if (r === 'victory') return 'win';
  if (r === 'defeat') return 'loss';
  return 'draw';
}
function resultLabel(r) {
  if (r === 'victory') return 'WIN';
  if (r === 'defeat') return 'LOSS';
  return 'DRAW';
}

function PlayerProfile() {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [brawlers, setBrawlers] = useState([]);
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  usePageMeta({
    title: player?.name ? `${player.name} Player Stats` : 'Players',
    description: player?.name ? `Brawl Stars stats for ${player.name}: trophies, brawlers and recent battles.` : undefined,
    noindex: true,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [p, br, bt] = await Promise.all([
          api.getPlayer(tag),
          api.getPlayerBrawlers(tag).catch(() => []),
          api.getPlayerBattles(tag, 25).catch(() => []),
        ]);
        if (cancelled) return;
        setPlayer(p);
        setBrawlers(br);
        setBattles(bt);
        rememberTag('brawl', p?.tag || tag, p?.name);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [tag]);

  const climb = useMemo(() => {
    const tips = [];
    const decided = battles.filter((b) => b.result === 'victory' || b.result === 'defeat');
    if (decided.length) {
      const wins = decided.filter((b) => b.result === 'victory').length;
      const wr = Math.round((wins / decided.length) * 100);
      tips.push({
        kind: wr >= 50 ? 'win' : 'warn',
        text: `You've won ${wins} of your last ${decided.length} decided battles (${wr}%). ${
          wr >= 50 ? 'Momentum is on your side — keep queuing your best modes.' : 'Cool off and switch to a stronger pick before you bleed trophies.'
        }`,
      });
    }
    const counts = {};
    battles.forEach((b) => { counts[b.brawler_name] = (counts[b.brawler_name] || 0) + 1; });
    const fav = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (fav) {
      tips.push({
        kind: 'win',
        text: `${fav[0]} is your most-played brawler recently (${fav[1]} of your last ${battles.length} games). Check its best maps on the Tier List.`,
      });
    }
    return tips;
  }, [battles]);

  if (loading) return <div className="page"><div className="wrap"><SkeletonHero /></div></div>;
  if (error) return <div className="page"><div className="wrap"><ErrorMessage message={error} /></div></div>;
  if (!player) return <div className="page"><div className="wrap"><ErrorMessage message="Player not found" /></div></div>;

  const topBrawlers = brawlers.slice(0, 4);
  const heroBrawler = brawlers[0];
  const moreCount = Math.max(0, brawlers.length - topBrawlers.length);

  return (
    <div className="page">
      <div className="wrap">
        <span className="back" onClick={() => navigate('/players')}>← Players</span>
        <RecentTags game="brawl" current={player.tag} onPick={(t) => navigate(`/players/${encodeURIComponent(`#${t}`)}`)} />

        <div className="bdhero">
          <div className="glow" />
          {heroBrawler ? (
            <span className="av circle" style={{ width: 96, height: 96, zIndex: 1 }}>
              <img src={brawlerImage(heroBrawler.name)} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            </span>
          ) : (
            <span className="pav" style={{ width: 96, height: 96, fontSize: 38, zIndex: 1 }}>{initial(player.name)}</span>
          )}
          <div className="who">
            <h1 style={{ fontSize: 32 }}>{player.name}</h1>
            <div className="tags">
              <span className="pill-chip">{player.tag}</span>
              {player.club_name && <span className="pill-chip">{player.club_name}</span>}
              {player.highest_trophies != null && (
                <span className="pill-chip lime">Peak {player.highest_trophies.toLocaleString()}</span>
              )}
            </div>
          </div>
          <div className="stats">
            <div className="stat"><div className="v">{player.trophies?.toLocaleString() ?? '—'}</div><div className="l">Trophies</div></div>
            <div className="stat"><div className="v">{pct(player.win_rate)}</div><div className="l">Win rate</div></div>
            <div className="stat"><div className="v">{player.victories_3vs3?.toLocaleString() ?? '—'}</div><div className="l">3v3 wins</div></div>
          </div>
        </div>

        <div className="bdcols">
          <div className="col-stack">
            <div className="panel">
              <h3><span className="ic" style={{ background: 'var(--surface-2)', color: 'var(--txt-soft)' }}>🕑</span>Recent battles</h3>
              {battles.length > 0 ? (
                <div className="recent-list">
                  {battles.map((b) => {
                    const rc = resultClass(b.result);
                    return (
                      <div key={b.battle_id} className={`recent-row ${rc}`}>
                        <Avatar name={b.brawler_name} size={38} />
                        <div className="rr-mid">
                          <div className="rr-b">{b.brawler_name}{b.is_star_player && ' ★'}</div>
                          <div className="rr-m">
                            {b.mode}{b.map ? ` · ${b.map}` : ''}
                            {b.battle_time ? ` · ${timeAgo(b.battle_time)}` : ''}
                          </div>
                        </div>
                        <div className="rr-res">
                          <span className={`res ${rc === 'win' ? 'w' : rc === 'loss' ? 'l' : 'd'}`}>{resultLabel(b.result)}</span>
                          <span className="rr-d">
                            {b.trophy_change != null ? (b.trophy_change > 0 ? `+${b.trophy_change}` : b.trophy_change) : '—'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-state">No recent battles tracked.</p>
              )}
            </div>

            <div className="panel">
              <h3><span className="ic" style={{ background: 'var(--surface-2)', color: 'var(--txt-soft)' }}>▦</span>Top brawlers</h3>
              {topBrawlers.length > 0 ? (
                <div className="mate-row" style={{ gap: 16 }}>
                  {topBrawlers.map((b) => (
                    <div key={b.brawler_id} className="mate" style={{ width: 60 }}>
                      <Avatar name={b.name} size={48} /><span className="nm">{b.name}</span>
                    </div>
                  ))}
                  {moreCount > 0 && (
                    <div className="mate" style={{ width: 60 }}>
                      <span className="av" style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--muted)' }}>+{moreCount}</span>
                      <span className="nm">more</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="empty-state">No brawler collection on file.</p>
              )}
            </div>
          </div>

          <div className="panel">
            <h3><span className="ic win">✓</span>How to climb</h3>
            <p className="subhead" style={{ margin: '0 0 14px' }}>From your recent battle log</p>
            <div className="climb-list">
              {climb.map((t, i) => (
                <div key={i} className={`climb-card ${t.kind}`}>
                  <div className="cc-ic">{t.kind === 'warn' ? '⚠' : '✓'}</div>
                  <div className="cc-body">
                    <p>{t.text}</p>
                  </div>
                </div>
              ))}
              <div className="climb-card beat">
                <div className="cc-ic">⚔</div>
                <div className="cc-body">
                  <p>Personalized counter advice — who you lose to and how to beat them — is coming soon. It needs head-to-head matchup data that isn't tracked yet.</p>
                  <span className="cc-cta" onClick={() => navigate('/brawlers')}>Browse the tier list →</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlayerProfile;
